import { useState } from "react"
import { useForm, FieldValues } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { useToast } from "@/contexts/ToastContext"

import { useAuth } from "@/modules/Auth/context/AuthContext"
import { createUser } from "@/modules/Users/service/UsersService"
import { parseFormToCadastro } from "@/modules/Users/utils/dataTransformers"
import { MemberFormFields } from "@/modules/Users/components/MemberFormFields"

import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import PageMainContainer from "@/components/Templates/PageMainContainer"
import { CADASTRO_PessoaDataType } from "@/types/membros"
import { motion } from "framer-motion"
import { Icon } from "@/components/Atoms/Icon"

export function CreateMemberPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const toast = useToast()

  const defaultValues: FieldValues = {
    email: "",
    cpf: "",
    nome_completo: "",
    nome_certificado: "",
    nome_cracha: "",
    cargo: "",
    departamento: "",
    perfil: "",
    administrador: false,
    status: true,
    cep: "",
    telefone: "",
    sexo: "",
    data_nascimento: "",
    chocolate_preferido: "",
    originador_id: user?.pesId || 0,
  }

  const form = useForm({
    defaultValues: defaultValues,
    mode: "onChange",
  })

  const isFormIncomplete = !form.formState.isValid

  const onSubmit = async (data: FieldValues) => {
    if (!user) {
      toast.danger("Usuário não autenticado.")
      return
    }

    setIsSubmitting(true)
    try {
      const dataForParsing: any = {
        ...data,
        emp_id: user.empId,
        originador_id: user.pesId,
      }

      const apiPayload: CADASTRO_PessoaDataType = parseFormToCadastro(dataForParsing)

      await createUser({
        user: apiPayload,
        emp_id: user.empId,
        originador_id: user.pesId,
      })

      toast.success("Usuário cadastrado com sucesso!")
      navigate("/users") // Redirect to users list page
    } catch (error) {
      toast.danger(error?.response?.data?.message || "Ocorreu um erro ao cadastrar o usuário.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.4 } },
  }

  return (
    <section className="w-full animate-fade-in py-10">
      <header className="mx-6 mx-auto flex w-[calc(100vw-20%)] max-w-[800px] flex-shrink-0 gap-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-orange-500"
        >
          <Icon name="arrow-left" />
        </button>
        <div className="flex flex-col gap-1">
          <motion.h1
            variants={itemVariants}
            className="text-2xl font-black md:text-4xl"
          >
            Novo usuário
          </motion.h1>
        </div>
      </header>
      <div className="mx-auto w-[calc(100vw-20%)] max-w-[800px]">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-8"
          >
            <MemberFormFields
              control={form.control as any}
              mode="create"
            />

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate("/users")}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={isFormIncomplete ? 0 : undefined}>
                    <Button
                      type="submit"
                      className="bg-orange-500 text-white hover:bg-orange-500 hover:text-white disabled:bg-zinc-700 disabled:text-zinc-400"
                      disabled={isSubmitting || isFormIncomplete}
                    >
                      {isSubmitting ? "Cadastrando..." : "Cadastrar"}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isFormIncomplete && !isSubmitting && (
                  <TooltipContent>
                    Preencha corretamente todos os campos obrigatórios.
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </form>
        </Form>
      </div>
    </section>
  )
}